import React, { useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import {
  Box,
  Paper,
  Stack,
  Button,
  Tooltip,
  Collapse,
  Typography,
} from "@mui/material";
import { Link } from "gatsby";
import { Trans, useTranslation } from "gatsby-plugin-react-i18next";

import getDocDetailService from "../services/getDocDetail.service";

function DocOverviewCardLisItem({ item }) {
  const { t } = useTranslation();

  const redirectTo = (link) => {
    if (!link) return;
    if (link === "") return;

    window.open(link, "_blank");
  };

  return (
    <li>
      <Typography
        color="secondary.light"
        variant="body2"
        fontSize="14px"
        lineHeight="1em"
        sx={{
          gap: "4px",
          display: "flex",
          alignItems: "center",
          textDecoration: item.underline ? "underline" : "none",
          "&:hover": {
            cursor: item.link ? "pointer" : "default",
          },
        }}
        onClick={() => redirectTo(item.link)}
      >
        {t(item.i18n_label)}
        {item.tooltip && (
          <Tooltip title={t(item.i18n_tooltip_label)}>
            <InfoRoundedIcon fontSize="small" color="primary" />
          </Tooltip>
        )}
      </Typography>
    </li>
  );
}

export default function DocOverviewCard({ type, widgetView = false}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(widgetView ? true : false);
  const documentation = getDocDetailService({ type });

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  if (!documentation) return;

  return (
    <Paper
      sx={{
        padding: "16px",
        minWidth: 240,
        minHeight: 300,
        height: isExpanded ? "100%" : "auto",
        borderRadius: "8px",
        border: "3px solid transparent",
        "&:hover": {
          transition: "border 300ms ease",
          border: "3px solid rgba(239, 96, 163, 1)",
        },
        bgcolor: "white",
      }}
    >
      <Box height="100%" display="flex" minHeight={310} flexDirection="column" justifyContent="space-between">
        <img
          src={`/images/${documentation.image}`}
          alt={documentation.key}
          style={{
            width: "100%",
            height: "auto",
            objectFit: "cover",
            aspectRatio: "21/9",
            borderRadius: "8px",
          }}
        />

        <Stack mt="12px">
          <Typography
            variant="h3"
            color="primary"
            fontFamily="Gotham-Bold"
            fontSize="24px"
            gutterBottom
          >
            {t(documentation.i18n_title)}
          </Typography>

          <Typography
              fontSize="14px"
              color="#333334"
              lineHeight="20px"
              sx={{
                a: {
                  color: '#EF60A3',
                  textDecoration: 'underline',
                },
              }}
            >
          <Trans i18nKey={documentation.i18n_description} components={[<Link to="/requirements" />]} />
          </Typography>
        </Stack>

        <Collapse
          in={isExpanded}
          sx={{ margin: isExpanded ? "12px 0 24px 0" : "12px 0px 0px 0px" }}
        >
          <Stack direction="column" gap="12px">
            <Stack backgroundColor="#FBFBFB" border="solid 1px #BABABA" borderRadius="1em" px="1em" py="1em" gap="1em">
              <Typography
                color="primary"
                variant="body2"
                fontFamily="Gotham-Medium"
                fontSize="16px"
              >
                {t("to_mexico")}
              </Typography>
              <ul style={{ marginLeft: "24px", marginBottom: 0 }}>
                {documentation.toMexico.map((item, index) => (
                  <DocOverviewCardLisItem
                    key={`${item.i18n_label}-${index}`}
                    item={item}
                  />
                ))}
              </ul>
            </Stack>
            <Stack backgroundColor="#FBFBFB" border="solid 1px #BABABA" borderRadius="1em" px="1em" py="1em" gap="1em">
              <Typography
                color="primary"
                variant="body2"
                fontFamily="Gotham-Medium"
                fontSize="16px"
              >
                {t("to_usa")}
              </Typography>
              <ul style={{ marginLeft: "24px", marginBottom: 0 }}>
                {documentation.toUSA.map((item, index) => (
                  <DocOverviewCardLisItem
                    key={`${item.i18n_label}-${index}`}
                    item={item}
                  />
                ))}
              </ul>
            </Stack>
            {documentation?.i18n_note && (
              <Typography color="secondary.light" variant="body2" fontSize="12px">
                {t(documentation.i18n_note)}
              </Typography>
            )}
          </Stack>
        </Collapse>

        {!widgetView && <Button
          sx={{
            "& span": {
              transform: isExpanded ? "rotate(180deg)" : "",
            },
          }}
          size="small"
          color="primary"
          variant="outlined"
          endIcon={<KeyboardArrowDownIcon />}
          onClick={toggleExpanded}
        >
          {isExpanded ? t("see_less") : t("see_more")}
        </Button>}
      </Box>
    </Paper>
  );
}
